/**
 * How long an unstored card conversion has left, for display.
 *
 * Mirrors `services/lib/domain/conversionRetention.ts`. Kept as its own tiny module rather than inlined
 * in the table so the wording is testable — the frontend has no component tests, and "6 days left" versus
 * "0 days left" on a file the customer can still download is exactly the kind of thing worth pinning.
 */

const DAY_MS = 86_400_000;

export interface Remaining {
  expired: boolean;
  daysRemaining: number;
  label: string;
  /** True inside the final day — the point at which the notice should start shouting. */
  urgent: boolean;
}

/** Null when there is nothing to say: a stored EFT, a live capture, or a row predating retention. */
export function timeRemaining(expiresOn: string | undefined, now: Date = new Date()): Remaining | null {
  if (!expiresOn) return null;
  const end = new Date(expiresOn).getTime();
  if (!Number.isFinite(end)) return null;
  const ms = end - now.getTime();
  if (ms <= 0) return { expired: true, daysRemaining: 0, label: 'Deleted', urgent: true };

  const days = Math.ceil(ms / DAY_MS);
  const hours = Math.ceil(ms / 3_600_000);
  const label =
    ms < 3_600_000 ? 'Deleted within the hour'
    : hours < 24 ? `${hours}h left`
    : `${days} day${days === 1 ? '' : 's'} left`;
  return { expired: false, daysRemaining: days, label, urgent: hours < 24 };
}
